import React, { useState, useEffect, useCallback, useRef } from "react";
import { Building2, Clock, FileText, Layers, MapPin, Printer, ShieldCheck, Truck, XIcon } from "lucide-react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { debounce } from "lodash";

import { createLh, getAllUniqueValue, getSingleLR, makeAutoConsignmentNoteNo, upddatLR } from "./lhServices";
import { validationForConsignmentNoteNo } from "./utils/validationForConsignmentNoteNo";

import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Separator } from "../../components/ui/separator";
import { Textarea } from "../../components/ui/textarea";
import { COMPANY_LEGAL_NAME, TITLE_HEADING_NAME } from "../../config/branding";

const initialFormData = {
  companyPAN: "",
  companyGST: "",
  companyAddress: "",
  email: "",
  consignmentNoteNo: "",
  consignmentNoteDate: "",
  origin: "",
  destination: "",
  vehicleNo: "",
  consignorAddress: "",
  consigneeAddress: "",
  invoiceDate: "",
  eWayBill: [""],
  goodsValue: [""],
  invoiceDetails: [""],
  packages: [""],
  commertialValues: [""],
  issuingOffice: "",
  packingMode: "",
  gstPayableBy: "consignor",
  date: new Date().toISOString().split("T")[0],
  customFields: [],
  remarks: "",
};

const DeliveryServiceForm = () => {
  const [formData, setFormData] = useState(initialFormData);
  const [loadingAndErrorConfiguration, setLoadingAndErrorConfiguration] = useState({ isLoading: false, isError: false });
  const [activeSuggestionField, setActiveSuggestionField] = useState(null);
  const [uniqueValue, setUniqueValue] = useState([]);
  const blurTimeoutRef = useRef(null);

  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const consignmentId = searchParams.get("id");

  const handleInputChange = useCallback((field, value, index) => {
    setFormData((prev) => {
      if (Array.isArray(prev[field]) && index !== undefined) {
        const newArray = [...prev[field]];
        newArray[index] = value;
        return { ...prev, [field]: newArray };
      }
      return { ...prev, [field]: value };
    });
  }, []);

  const handleSuggestionOpen = useCallback((field) => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }
    setActiveSuggestionField(field);
  }, []);

  const handleSuggestionClose = useCallback(() => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }
    blurTimeoutRef.current = setTimeout(() => setActiveSuggestionField(null), 120);
  }, []);

  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!id) {
      (async () => {
        const consignmentNoteNo = await makeAutoConsignmentNoteNo();
        setFormData((prev) => ({ ...prev, consignmentNoteNo }));
      })();
    } else {
      setFormData((prev) => ({ ...prev, consignmentNoteNo: id }));
    }
  }, [id]);

  const populateData = useCallback(
    (data) => {
      const { consignmentNoteDate, eWayBill, goodsValue, commertialValues, invoiceDetails, packages, ...rest } = data;

      setFormData({
        ...rest,
        eWayBill: consignmentId ? eWayBill : [""],
        goodsValue: consignmentId ? goodsValue : [""],
        invoiceDetails: consignmentId ? invoiceDetails : [""],
        commertialValues: consignmentId ? commertialValues : [""],
        packages: consignmentId ? packages : [""],
        consignmentNoteDate,
      });
    },
    [consignmentId]
  );

  useEffect(() => {
    if (id) {
      (async () => {
        try {
          const res = await getSingleLR(id);
          if (res) {
            populateData(res);
          }
        } catch (error) {
          setFormData((prev) => ({ ...prev, consignmentNoteNo: "" }));
          toast.error(
            error?.response?.data?.error +
              " Oops! It seems like the consignment note number you're looking for doesn't exist. Please check and try again."
          );
        }
      })();
    }
  }, [id, populateData]);

  useEffect(() => {
    if (!activeSuggestionField) return;

    (async () => {
      try {
        const res = await getAllUniqueValue(activeSuggestionField);
        setUniqueValue(res || []);
      } catch (error) {
        console.error(error);
        setUniqueValue([]);
      }
    })();
  }, [activeSuggestionField]);

  const validateFormData = (data) => {
    const { weight, rate, ...rest } = data;
    const emptyFields = Object.keys(rest).filter((key) => {
      const value = data[key];

      if (Array.isArray(value)) {
        return value.some((item) => {
          if (typeof item === "string") {
            return item.trim() === "";
          }
          if (item === null || item === undefined) {
            return true;
          }
          return String(item).trim() === "";
        });
      }

      if (typeof value === "string") {
        return value.trim() === "";
      }
      return value === "" || value === null || value === undefined;
    });

    if (emptyFields.length > 0) {
      toast.error(`Please fill in all required fields: ${emptyFields.join(", ")}`);
      return false;
    }
    return true;
  };

  const handlePrint = async () => {
    setLoadingAndErrorConfiguration({ isLoading: true, isError: false });
    const elementsToRemove = document.querySelectorAll("[data-no-print=\"true\"]");
    elementsToRemove.forEach((element) => element.remove());

    if (validateFormData(formData)) {
      try {
        await (id
          ? upddatLR(id, { data: { ...formData, consignmentNoteNo: id } })
          : createLh({ data: { ...formData, consignmentNoteNo: formData.consignmentNoteNo } }));

        const formElement = document.querySelector(".max-w-6xl");
        const printElement = formElement.cloneNode(true);

        const inputs = printElement.querySelectorAll("input, textarea");
        inputs.forEach((input) => {
          const parent = input.parentNode;

          if (input.type === "radio") {
            const container = document.createElement("div");
            container.style.display = "flex";
            container.style.alignItems = "center";
            container.style.gap = "8px";

            const icon = document.createElement("div");
            icon.innerHTML = input.checked ? "⦿" : "○";
            container.appendChild(icon);

            const label = parent.querySelector("label");
            if (label) {
              const labelDiv = document.createElement("div");
              labelDiv.textContent = label.textContent;
              container.appendChild(labelDiv);
            }

            parent.replaceChild(container, input);
          } else {
            const div = document.createElement("div");
            div.textContent = input.value;
            div.style.minHeight = "24px";
            div.style.padding = "4px 8px";
            div.style.border = "1px solid #e5e7eb";
            div.style.borderRadius = "4px";
            div.style.backgroundColor = "#ffffff";
            div.style.fontSize = input.style.fontSize || "14px";
            parent.replaceChild(div, input);
          }
        });

        printElement.style.position = "absolute";
        printElement.style.left = "-9999px";
        document.body.appendChild(printElement);

        const canvas = await html2canvas(printElement, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
        });

        document.body.removeChild(printElement);

        const imgWidth = 210;
        const pageHeight = 297;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        const pdf = new jsPDF("p", "mm", "a4");

        const nPages = Math.ceil(imgHeight / pageHeight);

        for (let page = 0; page < nPages; page++) {
          if (page > 0) {
            pdf.addPage();
          }

          const imgData = canvas.toDataURL("image/png");
          pdf.addImage(imgData, "PNG", 0, page === 0 ? 0 : -(page * pageHeight), imgWidth, imgHeight, "", "FAST");
        }

        pdf.save("delivery-service-form.pdf");
        window.location.reload();
      } catch (error) {
        toast.error(error?.response?.data?.error);
        console.error("Error generating PDF:", error);
        setLoadingAndErrorConfiguration({ isError: true, isLoading: false });
      } finally {
        setLoadingAndErrorConfiguration({ isError: false, isLoading: false });
      }
    } else {
      setLoadingAndErrorConfiguration({ isLoading: false, isError: false });
    }
  };

  useEffect(() => {
    if (formData.consignmentNoteNo && uniqueValue.includes(formData.consignmentNoteNo)) {
      toast.error("Consignment Note Number already exists");
      return;
    }

    if (!id) {
      const debouncedToast = debounce((consignmentNoteNo) => {
        if (consignmentNoteNo && consignmentNoteNo.length >= 10 && !validationForConsignmentNoteNo(consignmentNoteNo)) {
          toast.error(
            "Invalid Consignment Note Number. Format should be: IDP/<current year-previous year>/<number>. Example: IDP/22-23/12345678901234567890"
          );
        }
      }, 900);

      debouncedToast(formData.consignmentNoteNo);
    }
  }, [formData.consignmentNoteNo, uniqueValue, id]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 py-10">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl space-y-6 rounded-3xl bg-white/95 p-6 shadow-2xl">
          <div className="space-y-3">
            <Badge variant="secondary" className="w-fit rounded-full px-4 py-1 text-xs font-semibold">
              Consignment workflow
            </Badge>
            <div className="space-y-1">
              <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Delivery Service Consignment Note</h1>
              <p className="text-sm text-muted-foreground">
                {COMPANY_LEGAL_NAME} · Gurgaon jurisdiction
              </p>
            </div>
          </div>

          <Card className="border border-slate-200 shadow-sm">
            <CardHeader className="pb-4">
              <SectionTitle icon={ShieldCheck} title="Company snapshot" description="PAN, GST and caution note" />
            </CardHeader>
            <CardContent className="grid gap-6 lg:grid-cols-3">
              <div className="space-y-4">
                <div className="relative space-y-2">
                  <Label>Company PAN</Label>
                  <Input
                    type="text"
                    value={formData.companyPAN}
                    placeholder="Enter PAN"
                    onChange={(e) => handleInputChange("companyPAN", e.target.value)}
                    onFocus={() => handleSuggestionOpen("companyPAN")}
                    onBlur={handleSuggestionClose}
                  />
                  <SuggestionList
                    isActive={activeSuggestionField === "companyPAN"}
                    options={uniqueValue}
                    filterValue={formData.companyPAN}
                    onSelect={(value) => handleInputChange("companyPAN", value)}
                  />
                </div>
                <div className="relative space-y-2">
                  <Label>GST Number</Label>
                  <Input
                    type="text"
                    value={formData.companyGST}
                    placeholder="Enter GST"
                    onChange={(e) => handleInputChange("companyGST", e.target.value)}
                    onFocus={() => handleSuggestionOpen("companyGST")}
                    onBlur={handleSuggestionClose}
                  />
                  <SuggestionList
                    isActive={activeSuggestionField === "companyGST"}
                    options={uniqueValue}
                    filterValue={formData.companyGST}
                    onSelect={(value) => handleInputChange("companyGST", value)}
                  />
                </div>
              </div>

              <div className="space-y-3 rounded-2xl border border-dashed border-slate-200 bg-slate-50/80 p-4 text-sm text-slate-600">
                <Badge variant="destructive" className="w-fit">
                  Caution
                </Badge>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Subject to Gurgaon jurisdiction only
                </p>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Accounts copy · Owner risk</p>
                <p className="text-sm leading-relaxed">
                  This consignment will not be detained, diverted, re-routed or re-booked without consignee bank&apos;s written
                  permission and will be delivered at the destination.
                </p>
              </div>

              <div className="space-y-4">
                <div className="relative space-y-2">
                  <Label>Company address</Label>
                  <Textarea
                    value={formData.companyAddress}
                    placeholder="Add company address"
                    className="min-h-[110px] resize-none"
                    onChange={(e) => handleInputChange("companyAddress", e.target.value)}
                    onFocus={() => handleSuggestionOpen("companyAddress")}
                    onBlur={handleSuggestionClose}
                  />
                  <SuggestionList
                    isActive={activeSuggestionField === "companyAddress"}
                    options={uniqueValue}
                    filterValue={formData.companyAddress}
                    onSelect={(value) => handleInputChange("companyAddress", value)}
                  />
                </div>
                <div className="relative space-y-2">
                  <Label>Official email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    placeholder="name@email.com"
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    onFocus={() => handleSuggestionOpen("email")}
                    onBlur={handleSuggestionClose}
                  />
                  <SuggestionList
                    isActive={activeSuggestionField === "email"}
                    options={uniqueValue}
                    filterValue={formData.email}
                    onSelect={(value) => handleInputChange("email", value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
            <div className="space-y-6">
              <Card className="border border-slate-200">
                <CardHeader className="pb-4">
                  <SectionTitle
                    icon={Building2}
                    title="Shipment parties & route"
                    description="Consignor, consignee and LR information"
                  />
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid gap-6 lg:grid-cols-2">
                    <div className="relative space-y-2">
                      <Label>Consignor address</Label>
                      <Textarea
                        value={formData.consignorAddress}
                        placeholder="Enter consignor address"
                        className="min-h-[120px]"
                        onChange={(e) => handleInputChange("consignorAddress", e.target.value)}
                        onFocus={() => handleSuggestionOpen("consignorAddress")}
                        onBlur={handleSuggestionClose}
                      />
                      <SuggestionList
                        isActive={activeSuggestionField === "consignorAddress"}
                        options={uniqueValue}
                        filterValue={formData.consignorAddress}
                        onSelect={(value) => handleInputChange("consignorAddress", value)}
                      />
                    </div>
                    <div className="relative space-y-2">
                      <Label>Consignee address</Label>
                      <Textarea
                        value={formData.consigneeAddress}
                        placeholder="Enter consignee address"
                        className="min-h-[120px]"
                        onChange={(e) => handleInputChange("consigneeAddress", e.target.value)}
                        onFocus={() => handleSuggestionOpen("consigneeAddress")}
                        onBlur={handleSuggestionClose}
                      />
                      <SuggestionList
                        isActive={activeSuggestionField === "consigneeAddress"}
                        options={uniqueValue}
                        filterValue={formData.consigneeAddress}
                        onSelect={(value) => handleInputChange("consigneeAddress", value)}
                      />
                    </div>
                  </div>

                  <div className="grid gap-6 lg:grid-cols-[1.5fr_280px]">
                    <div className="grid gap-4 sm:grid-cols-3">
                      <div className="relative space-y-2">
                        <Label className="flex items-center gap-2 text-sm font-medium">
                          <MapPin className="h-4 w-4 text-primary" /> Origin
                        </Label>
                        <Input
                          value={formData.origin}
                          placeholder="Origin"
                          onChange={(e) => handleInputChange("origin", e.target.value)}
                          onFocus={() => handleSuggestionOpen("origin")}
                          onBlur={handleSuggestionClose}
                        />
                        <SuggestionList
                          isActive={activeSuggestionField === "origin"}
                          options={uniqueValue}
                          filterValue={formData.origin}
                          onSelect={(value) => handleInputChange("origin", value)}
                        />
                      </div>
                      <div className="relative space-y-2">
                        <Label className="flex items-center gap-2 text-sm font-medium">
                          <MapPin className="h-4 w-4 rotate-180 text-primary" /> Destination
                        </Label>
                        <Input
                          value={formData.destination}
                          placeholder="Destination"
                          onChange={(e) => handleInputChange("destination", e.target.value)}
                          onFocus={() => handleSuggestionOpen("destination")}
                          onBlur={handleSuggestionClose}
                        />
                        <SuggestionList
                          isActive={activeSuggestionField === "destination"}
                          options={uniqueValue}
                          filterValue={formData.destination}
                          onSelect={(value) => handleInputChange("destination", value)}
                        />
                      </div>
                      <div className="relative space-y-2">
                        <Label className="flex items-center gap-2 text-sm font-medium">
                          <Truck className="h-4 w-4 text-primary" /> Vehicle no.
                        </Label>
                        <Input
                          value={formData.vehicleNo}
                          placeholder="Vehicle number"
                          onChange={(e) => handleInputChange("vehicleNo", e.target.value)}
                          onFocus={() => handleSuggestionOpen("vehicleNo")}
                          onBlur={handleSuggestionClose}
                        />
                        <SuggestionList
                          isActive={activeSuggestionField === "vehicleNo"}
                          options={uniqueValue}
                          filterValue={formData.vehicleNo}
                          onSelect={(value) => handleInputChange("vehicleNo", value)}
                        />
                      </div>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">Consignment note</p>
                      <div className="mt-3 space-y-4">
                        <div className="relative space-y-2">
                          <Label>Number</Label>
                          <Input
                            value={formData.consignmentNoteNo}
                            placeholder="Auto generated"
                            onChange={(e) => handleInputChange("consignmentNoteNo", e.target.value)}
                            disabled={Boolean(id)}
                            onFocus={() => handleSuggestionOpen("consignmentNoteNo")}
                            onBlur={handleSuggestionClose}
                          />
                          <SuggestionList
                            isActive={activeSuggestionField === "consignmentNoteNo"}
                            options={uniqueValue}
                            filterValue={formData.consignmentNoteNo}
                            onSelect={(value) => handleInputChange("consignmentNoteNo", value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Date</Label>
                          <Input
                            type="date"
                            value={formData.consignmentNoteDate}
                            onChange={(e) => handleInputChange("consignmentNoteDate", e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border border-slate-200">
                <CardHeader className="pb-4">
                  <SectionTitle
                    icon={Layers}
                    title="Package manifest"
                    description="Packages, invoices and goods valuation"
                  />
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="overflow-hidden rounded-2xl border border-slate-200">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-xs uppercase tracking-wide text-muted-foreground">
                        <tr>
                          <th className="p-3 text-left">Packages</th>
                          <th className="p-3 text-left">Description & documents</th>
                          <th className="p-3 text-left">Weight (Actual / Chargeable)</th>
                          <th className="p-3 text-left">Rate</th>
                          <th className="p-3 text-left">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="align-top">
                          <td className="border-t border-slate-100 p-4">
                            <div className="space-y-3">
                              {formData.packages.map((item, index) => (
                                <Input
                                  key={`package-${index}`}
                                  type="number"
                                  value={item}
                                  placeholder="Qty"
                                  className="h-9"
                                  onChange={(e) => handleInputChange("packages", e.target.value, index)}
                                />
                              ))}
                              <div className="rounded-xl bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-600">
                                Total: {formData.packages.reduce((prev, curr) => Number(prev) + Number(curr || 0), 0)}
                              </div>
                            </div>
                          </td>
                          <td className="border-t border-slate-100 p-4">
                            <div className="space-y-4">
                              <div>
                                <p className="text-xs font-semibold uppercase text-muted-foreground">Invoice references</p>
                                <p className="text-sm text-slate-600">{formData.invoiceDetails.filter(Boolean).join(", ") || "As per invoice attached"}</p>
                              </div>
                              <div className="space-y-2">
                                <Label className="text-xs">eWay bill numbers</Label>
                                <div className="space-y-2">
                                  {formData.eWayBill.map((item, index) => (
                                    <Input
                                      key={`eway-${index}`}
                                      value={item}
                                      placeholder="Enter 12-digit eWay bill"
                                      maxLength={12}
                                      className="h-9"
                                      onChange={(e) => handleInputChange("eWayBill", e.target.value, index)}
                                    />
                                  ))}
                                </div>
                              </div>
                              <div className="space-y-2">
                                <Label className="text-xs">Commercial no.</Label>
                                {formData.commertialValues.map((item, index) => (
                                  <Input
                                    key={`commercial-${index}`}
                                    value={item}
                                    placeholder="Commercial value reference"
                                    className="h-9"
                                    onChange={(e) => handleInputChange("commertialValues", e.target.value, index)}
                                  />
                                ))}
                              </div>
                              <div className="space-y-2">
                                <Label className="text-xs">Goods value</Label>
                                {formData.goodsValue.map((item, index) => (
                                  <Input
                                    key={`goods-${index}`}
                                    type="number"
                                    value={item}
                                    placeholder="₹"
                                    className="h-9"
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      if (!isNaN(value)) {
                                        handleInputChange("goodsValue", value, index);
                                      }
                                    }}
                                  />
                                ))}
                              </div>
                            </div>
                          </td>
                          <td className="border-t border-slate-100 p-4">
                            <div className="rounded-xl border border-dashed border-slate-200 p-4 text-center text-xs text-muted-foreground">
                              Weight captured as per manifest
                            </div>
                          </td>
                          <td className="border-t border-slate-100 p-4">
                            <div className="flex flex-col items-center gap-2 text-xs font-semibold uppercase text-slate-500">
                              <span>Freight</span>
                              <span>Other</span>
                              <span>GST</span>
                            </div>
                          </td>
                          <td className="border-t border-slate-100 p-4">
                            <div className="flex flex-col items-center gap-2 text-xs font-semibold uppercase text-slate-500">
                              <span>To</span>
                              <span>Be</span>
                              <span>Billed</span>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              <Card className="border border-slate-200">
                <CardHeader className="pb-3">
                  <SectionTitle title="Approvals & signatures" description="Date, party signature and system note" />
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid gap-6 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Date</Label>
                      <Input type="date" value={formData.date} onChange={(e) => handleInputChange("date", e.target.value)} />
                      <p className="text-xs text-muted-foreground">System date for LR generation</p>
                    </div>
                    <div className="flex flex-col justify-between">
                      <p className="text-sm font-semibold text-slate-700">For, {TITLE_HEADING_NAME} Delivery Services Pvt. Ltd.</p>
                      <p className="text-xs text-muted-foreground">Authorized Signatory</p>
                    </div>
                  </div>
                  <Separator />
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-700">Party signature</p>
                      <p className="text-xs text-muted-foreground">Sign & stamp (if applicable)</p>
                    </div>
                    <div className="rounded-full bg-slate-100 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      System generated LR · signature not required
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-6">
              <Card className="border border-slate-200">
                <CardHeader className="pb-4">
                  <SectionTitle icon={MapPin} title="Operations control" description="Issuing office & packing mode" />
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="relative space-y-2">
                    <Label>Issuing office / agent</Label>
                    <Input
                      value={formData.issuingOffice}
                      placeholder="Issuing office"
                      onChange={(e) => handleInputChange("issuingOffice", e.target.value)}
                      onFocus={() => handleSuggestionOpen("issuingOffice")}
                      onBlur={handleSuggestionClose}
                    />
                    <SuggestionList
                      isActive={activeSuggestionField === "issuingOffice"}
                      options={uniqueValue}
                      filterValue={formData.issuingOffice}
                      onSelect={(value) => handleInputChange("issuingOffice", value)}
                    />
                  </div>
                  <div className="relative space-y-2">
                    <Label>Mode of packing</Label>
                    <Input
                      value={formData.packingMode}
                      placeholder="Packing mode"
                      onChange={(e) => handleInputChange("packingMode", e.target.value)}
                      onFocus={() => handleSuggestionOpen("packingMode")}
                      onBlur={handleSuggestionClose}
                    />
                    <SuggestionList
                      isActive={activeSuggestionField === "packingMode"}
                      options={uniqueValue}
                      filterValue={formData.packingMode}
                      onSelect={(value) => handleInputChange("packingMode", value)}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="border border-slate-200">
                <CardHeader className="pb-4">
                  <SectionTitle icon={FileText} title="Invoices & documents" description="Track each invoice line" />
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    {formData.invoiceDetails.map((inv, index) => (
                      <div key={`invoice-${index}`} className="flex items-center gap-3">
                        <Input
                          value={inv}
                          placeholder="Invoice no."
                          onChange={(e) => {
                            const newInvoiceDetails = [...formData.invoiceDetails];
                            newInvoiceDetails[index] = e.target.value;
                            handleInputChange("invoiceDetails", newInvoiceDetails);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && inv.trim() !== "") {
                              const newInvoiceDetails = [...formData.invoiceDetails, ""];
                              handleInputChange("invoiceDetails", newInvoiceDetails);
                              handleInputChange("packages", [...formData.packages, ""]);
                              handleInputChange("eWayBill", [...formData.eWayBill, ""]);
                              handleInputChange("goodsValue", [...formData.goodsValue, ""]);
                              handleInputChange("commertialValues", [...formData.commertialValues, ""]);
                            }
                          }}
                        />
                        {formData.invoiceDetails.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => {
                              const ids = [...formData.invoiceDetails];
                              ids.splice(index, 1);
                              const pack = [...formData.packages];
                              pack.splice(index, 1);
                              const eway = [...formData.eWayBill];
                              eway.splice(index, 1);
                              const goods = [...formData.goodsValue];
                              goods.splice(index, 1);
                              const comm = [...formData.commertialValues];
                              comm.splice(index, 1);
                              handleInputChange("invoiceDetails", ids);
                              handleInputChange("packages", pack);
                              handleInputChange("eWayBill", eway);
                              handleInputChange("goodsValue", goods);
                              handleInputChange("commertialValues", comm);
                            }}
                            data-no-print="true"
                          >
                            <XIcon className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      if (formData.invoiceDetails[formData.invoiceDetails.length - 1].trim() !== "") {
                        handleInputChange("invoiceDetails", [...formData.invoiceDetails, ""]);
                        handleInputChange("packages", [...formData.packages, ""]);
                        handleInputChange("eWayBill", [...formData.eWayBill, ""]);
                        handleInputChange("goodsValue", [...formData.goodsValue, ""]);
                        handleInputChange("commertialValues", [...formData.commertialValues, ""]);
                      }
                    }}
                    data-no-print="true"
                  >
                    Add invoice detail
                  </Button>
                  <div className="space-y-2">
                    <Label>Invoice date</Label>
                    <Input
                      type="date"
                      value={formData.invoiceDate}
                      onChange={(e) => handleInputChange("invoiceDate", e.target.value)}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="border border-slate-200">
                <CardHeader className="pb-4">
                  <SectionTitle title="Tax & remarks" description="GST responsibility and notes" />
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>GST payable by</Label>
                    <RadioGroup value={formData.gstPayableBy} onValueChange={(value) => handleInputChange("gstPayableBy", value)}>
                      {["consignor", "consignee", "transporter"].map((option) => (
                        <label key={option} className="flex items-center gap-3 rounded-xl border border-slate-200 px-3 py-2 text-sm capitalize">
                          <RadioGroupItem value={option} />
                          {option}
                        </label>
                      ))}
                    </RadioGroup>
                  </div>
                  <div className="space-y-2">
                    <Label>Remarks</Label>
                    <Textarea
                      value={formData.remarks}
                      placeholder="Add any special remarks"
                      className="min-h-[80px]"
                      onChange={(e) => handleInputChange("remarks", e.target.value)}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      <div className="print:hidden">
        <Button
          onClick={handlePrint}
          className="fixed bottom-10 right-10 flex items-center gap-2 shadow-xl"
          size="lg"
          data-no-print="true"
        >
          {loadingAndErrorConfiguration?.isLoading ? (
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 animate-spin" />
              <span>Preparing PDF...</span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Printer className="h-4 w-4" />
              <span>Generate PDF</span>
            </div>
          )}
        </Button>
      </div>
    </div>
  );
};

const SectionTitle = ({ icon: Icon, title, description }) => (
  <div className="flex items-center gap-3">
    {Icon ? (
      <div className="rounded-full bg-primary/10 p-2 text-primary">
        <Icon className="h-4 w-4" />
      </div>
    ) : null}
    <div>
      <CardTitle className="text-base font-semibold text-slate-900">{title}</CardTitle>
      {description ? <CardDescription className="text-xs text-muted-foreground">{description}</CardDescription> : null}
    </div>
  </div>
);

const SuggestionList = ({ isActive, options = [], filterValue = "", onSelect }) => {
  if (!isActive || !options.length) return null;

  const normalizedQuery = (filterValue || "").toLowerCase();
  const filtered = options
    .filter(Boolean)
    .filter((option) => String(option).toLowerCase().includes(normalizedQuery));

  if (!filtered.length) return null;

  return (
    <Card className="absolute left-0 right-0 top-full z-50 mt-2 border border-border bg-popover text-popover-foreground shadow-2xl">
      <ScrollArea className="max-h-60">
        <div className="divide-y divide-border">
          {filtered.map((result, index) => (
            <button
              key={`${result}-${index}`}
              type="button"
              className="flex w-full items-center px-3 py-2 text-left text-sm hover:bg-muted"
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect?.(result);
              }}
            >
              {result}
            </button>
          ))}
        </div>
      </ScrollArea>
    </Card>
  );
};

export default DeliveryServiceForm;

